/**
 * @file       Month/year component
 * @version    2.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } 
    from '@angular/core';
import { Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } 
  from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } 
  from '@angular/material/core';
import { DateTime } from 'luxon';
import { MONTH_YEAR_FORMAT } from '@app/constants/Format.constant';

@Component(
{
    selector: 'month-year-picker',
    templateUrl: './month.component.html',
    providers: [
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: MONTH_YEAR_FORMAT }
    ],
    standalone: false
})
/**
 * @desc   - Compponent for month year picker
 */  
class MonthYearPickerComponent implements OnInit 
{
    monthYearControl = new FormControl<DateTime | null>(null);
    @Input() disabled: boolean = false;
    @Output() monthYearChange = new EventEmitter<DateTime>();
    @Output() clearPressed = new EventEmitter<void>();

    @ViewChild('picker') picker: MatDatepicker<DateTime>;

    subscriptions: Subscription[] = [];
    //___________________ P U B L I C  M E T H O D S ___________________________
    
    /**
     * @desc   - initialization of the month-year-picker page
     * @access - public
     * @param  -
     * @return - 
     */ 
    ngOnInit(): void 
    {
        let subscription: any;

        subscription = this.monthYearControl.valueChanges.subscribe(
            (value: DateTime | null) => 
            {
                if (value instanceof DateTime && value.isValid) 
                {
                    this.monthYearChange.emit(value);
                }
            }
        );

        this.subscriptions.push(subscription);
    }

    /**
     * @desc   - component destruction
     * @access - public
     * @param  -
     * @return -
     */
    ngOnDestroy(): void
    {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * @desc   - changes to the  month-year-picker page
     * @access - public
     * @param  -
     * @return - 
     */  
    ngOnChanges(): void 
    {
        this.disabled ? this.monthYearControl.disable() : 
                        this.monthYearControl.enable();
    }

    /**
     * @desc  - handle the user typing in the field
     * @access - public
     * @param - event: the value of the input field
     * @return -
     */
    onInputBlur
    (
        event: Event
    ): void 
    {
        let input: HTMLInputElement;
        let value: string;

        input = event.target as HTMLInputElement;
        value = input.value;
        
        if (value) 
        {
            let parsedDate = DateTime.fromFormat(value, 'MM/yyyy');
            if (!parsedDate.isValid && value.length === 5) 
            {
                parsedDate = DateTime.fromFormat(value, 'MM/yy');
            }
            if (!parsedDate.isValid) 
            {
                parsedDate = DateTime.fromFormat(value, 'M/yyyy');
            }

            if (parsedDate.isValid) 
            {
                parsedDate = parsedDate.set({ day: 1 });
                this.monthYearControl.setValue(parsedDate);
            }
        }
    }

    /**
     * @desc   - handle the user typing in the field
     * @access - public
     * @param  - normalizedMonth: the value of the input field
     * @return -
     */
    chosenYearHandler
    (
        normalizedYear: DateTime | Date
    ) 
    {
        let ctrlValue: DateTime;
        let yearToSet: DateTime;
        
        ctrlValue = this.monthYearControl.value || DateTime.local();

        if (!(normalizedYear instanceof DateTime)) 
        {
            yearToSet = DateTime.fromJSDate(normalizedYear);
        } 
        else 
        {
            yearToSet = normalizedYear;
        }

        ctrlValue = ctrlValue.set({ year: yearToSet.year });
        this.monthYearControl.setValue(ctrlValue);
    }

    /**
     * @desc   - handle the user typing in the field
     * @access - public
     * @param  - normalizedMonth: the value of the input field
     * @param  - datepicker: the value of the input field
     * @return -
     */
    chosenMonthHandler
    (
        normalizedMonth: DateTime | Date,
        datepicker: MatDatepicker<any>
    ) 
    {
        let ctrlValue: DateTime;
        let monthToSet: DateTime;

        ctrlValue = this.monthYearControl.value || DateTime.local();

        if (!(normalizedMonth instanceof DateTime)) 
        {
            monthToSet = DateTime.fromJSDate(normalizedMonth);
        } 
        else 
        {
            monthToSet = normalizedMonth;
        }

        ctrlValue = ctrlValue.set(
        {
            month: monthToSet.month,
            day: 1
        });

        this.monthYearControl.setValue(ctrlValue);
        datepicker.close();
        this.monthYearChange.emit(ctrlValue);
    }

    /**
     * @desc   - handle the user typing in the field
     * @access - public
     * @param  -
     * @return -
     */
    disable()
    {
        this.disabled = true;
        this.monthYearControl.disable();
    }

    /**
     * @desc   - handle the user typing in the field
     * @access - public
     * @param  -
     * @return -
     */
    enable()
    {
        this.disabled = false;
        this.monthYearControl.enable();
    }

    /**
     * @desc   - handle the user typing in the field
     * @access - public
     * @param  -
     * @return -
     */
    clear() 
    {
        this.monthYearControl.setValue(null);
        this.clearPressed.emit();
    }

    /**
     * @desc   - clears the user typing in the field
     * @access - public
     * @param  -
     * @return -
     */
    clearWithoutEmitting(): void 
    {
        this.monthYearControl.reset();
    }
}

export  { MonthYearPickerComponent };